export type Language = 'en' | 'da' | 'sv' | 'de' | 'nb'

const LOCALES: Record<Language, string> = {
  en: 'en-US',
  da: 'da-DK',
  sv: 'sv-SE',
  de: 'de-DE',
  nb: 'nb-NO',
}

export function localeFor(lang: Language): string {
  return LOCALES[lang]
}

const formatters = new Map<string, Intl.NumberFormat>()

/** `currency` may be whatever a misconfigured place/cart/change payload
 *  contains (a non-ISO code such as `"kr"`, or `""`) — `Intl.NumberFormat`
 *  throws `RangeError` for those. Fall back to plain number formatting
 *  rather than let one bad currency code take down every render on this
 *  till, and cache the fallback decision so it isn't re-thrown every time. */
function buildFormatter(locale: string, currency: string): Intl.NumberFormat {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  } catch {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }
}

export function amountCurrency(amount: number, currency: string, locale: string): string {
  const key = `${locale}:${currency}`
  let formatter = formatters.get(key)
  if (!formatter) {
    formatter = buildFormatter(locale, currency)
    formatters.set(key, formatter)
  }
  return formatter.format(amount).replace(/\u00A0/g, ' ')
}

export interface MoneyPart {
  value: string
  /** The currency sign or code, styled quieter than the figure. */
  unit: boolean
}

const wholeFormatters = new Map<string, Intl.NumberFormat>()

/**
 * An amount as parts, for display: no decimals when there are no øre
 * ("DKK 163", "163 kr."), two when there are ("DKK 92.50"), and the currency
 * marked so it can be set smaller. A price without ".00" and with a quiet
 * currency reads as less, which is the point on a screen the customer pays
 * from. Order follows the locale. Same fallback as amountCurrency for a
 * currency Intl does not know.
 */
export function moneyParts(amount: number, currency: string, locale: string): MoneyPart[] {
  const cents = Math.round(amount * 100) % 100 !== 0
  const key = `${locale}:${currency}:${cents ? 2 : 0}`
  let formatter = wholeFormatters.get(key)
  if (!formatter) {
    const digits = cents ? 2 : 0
    try {
      formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })
    } catch {
      formatter = new Intl.NumberFormat(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits })
    }
    wholeFormatters.set(key, formatter)
  }
  return formatter.formatToParts(amount).map((part) => ({
    value: part.value.replace(/\u00A0/g, ' '),
    unit: part.type === 'currency',
  }))
}
